// frontend/src/api/userClient.ts
import { apiFetch } from "./baseClient";
import { LoginResponse, UserInfo } from "../model/backendTypes";

// When REACT_APP_AUTH_BYPASS=true we keep using the mock login/logout endpoints.
// In normal Auth0 mode, identity comes from the JWT and we use /api/me instead.
const BYPASS = process.env.REACT_APP_AUTH_BYPASS === "true";

/**
 * Production-style "who am I?" endpoint.
 *
 * In Auth0 mode, the backend derives the user from the Bearer token and returns
 * the current user + active project (LoginResponse).
 *
 * This expects a backend route:
 *   GET /api/me
 * protected by Auth0 (Depends(get_current_user)).
 */
export async function fetchCurrentUser(): Promise<LoginResponse> {
  const resp = await apiFetch("/api/me", {
    method: "GET",
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `HTTP ${resp.status}`);
  }

  return (await resp.json()) as LoginResponse;
}

/**
 * Legacy mock login by name.
 *
 * - In BYPASS mode (REACT_APP_AUTH_BYPASS=true):
 *     Uses the old /api/login/mock endpoint with a simple name-based login.
 *
 * - In Auth0 mode:
 *     You should NOT call this anymore; login is handled by Auth0 in the frontend.
 *     If it is called anyway, we delegate to fetchCurrentUser() so that the caller
 *     still receives a LoginResponse based on the current token.
 */
export async function loginByName(name: string): Promise<LoginResponse> {
  if (BYPASS) {
    const resp = await apiFetch("/api/login/mock", {
      method: "POST",
      body: { name } as any,
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || `HTTP ${resp.status}`);
    }

    return (await resp.json()) as LoginResponse;
  }

  console.warn(
    "[userClient] loginByName() was called in Auth0 mode. " +
    "Login is handled by Auth0; falling back to /api/me."
  );
  return fetchCurrentUser();
}

/**
 * Logout:
 *
 * - In BYPASS mode:
 *     Calls /api/logout/mock so the mock backend can clear its user state.
 *
 * - In Auth0 mode:
 *     Logout is typically handled entirely on the frontend via Auth0's logout().
 *     This function becomes a no-op unless you implement a real /api/logout.
 */
export async function logoutUser(user: UserInfo): Promise<void> {
  if (BYPASS) {
    const resp = await apiFetch("/api/logout/mock", {
      method: "POST",
      body: { user_id: user.id } as any,
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || `HTTP ${resp.status}`);
    }

    return;
  }

  // Auth0 mode: usually no backend logout necessary (stateless JWT auth).
  // If you later add a real /api/logout, you can call it here with apiFetch.
  console.warn(
    "[userClient] logoutUser() called in Auth0 mode. " +
    "Backend logout is not implemented; use Auth0 logout() on the frontend."
  );
}

export async function logoutBackend(): Promise<void> {
  // Token is automatically attached by apiFetch via Auth0
  await apiFetch("/api/logout", {
    method: "POST",
  });
}